<script setup lang="ts">
import type {
  IEquipmentFailureTypeUpsertForm,
  IEquipmentRepairActionUpsertForm,
  IEquipmentTypeUpsertForm,
  IInspectionPointUpsertForm,
} from '@/types/hvl/forms'
import { type FormInstance, type FormRules } from 'element-plus'
import { reactive, ref } from 'vue'
import type {
  IEquipmentType,
  IEquipmentTypeFailureType,
  IEquipmentTypeRepairAction,
  IInspectionPoint,
} from '@/types/hvl/EquipmentType'
import { useEquipmentType } from '@/composables/hvl/equipment-type'
import { Delete } from '@element-plus/icons-vue'

const {
  createEquipmentTypeMutation,
  updateEquipmentTypeMutation,
  deleteInspectionPointsMutation,
  deleteFailureTypesMutation,
  deleteRepairActionsMutation,
} = useEquipmentType()

const props = defineProps<{ equipmentType?: IEquipmentType }>()

const model = defineModel<boolean>()

const equipmentTypeFormRef = ref<FormInstance>()
const equipmentTypeForm = reactive<IEquipmentTypeUpsertForm>({
  name: '',
  inspection_points: Array<IInspectionPointUpsertForm>(),
  failure_types: Array<IEquipmentFailureTypeUpsertForm>(),
  repair_actions: Array<IEquipmentRepairActionUpsertForm>(),
})
const deletedInspectionPoints = ref<IInspectionPointUpsertForm[]>([])
const deletedFailureTypes = ref<IEquipmentFailureTypeUpsertForm[]>([])
const deletedRepairActions = ref<IEquipmentRepairActionUpsertForm[]>([])

const rules = reactive<FormRules<IEquipmentTypeUpsertForm>>({
  name: [{ required: true, message: 'Name is required.', trigger: ['blur', 'change'] }],
})

const initForm = async () => {
  if (!props.equipmentType) {
    equipmentTypeForm.name = ''
    equipmentTypeForm.inspection_points = Array<IInspectionPointUpsertForm>()
    equipmentTypeForm.failure_types = Array<IEquipmentFailureTypeUpsertForm>()
    equipmentTypeForm.repair_actions = Array<IEquipmentRepairActionUpsertForm>()
    return
  }

  equipmentTypeForm.name = props.equipmentType.name
  if (props.equipmentType.inspection_points.length > 0) {
    props.equipmentType.inspection_points.forEach((ip: IInspectionPoint) => {
      equipmentTypeForm.inspection_points.push({
        key: ip.id,
        id: ip.id,
        name: ip.name,
      })
    })
  }

  if (props.equipmentType.failure_types.length > 0) {
    props.equipmentType.failure_types.forEach((ft: IEquipmentTypeFailureType) => {
      equipmentTypeForm.failure_types.push({
        key: ft.id,
        id: ft.id,
        description: ft.description,
      })
    })
  }

  if (props.equipmentType.repair_actions.length > 0) {
    props.equipmentType.repair_actions.forEach((ra: IEquipmentTypeRepairAction) => {
      equipmentTypeForm.repair_actions.push({
        key: ra.id,
        id: ra.id,
        description: ra.description,
      })
    })
  }
}

const addInspectionPoint = () => {
  equipmentTypeForm.inspection_points.push({
    key: Date.now(),
    name: '',
  })
}

const addFailureType = () => {
  equipmentTypeForm.failure_types.push({
    key: Date.now(),
    description: '',
  })
}

const addRepairAction = () => {
  equipmentTypeForm.repair_actions.push({
    key: Date.now(),
    description: '',
  })
}

const removeInspectionPoint = (ip: IInspectionPointUpsertForm) => {
  const index = equipmentTypeForm.inspection_points.indexOf(ip)
  if (index !== -1) {
    const deletedInspectionPoint = equipmentTypeForm.inspection_points.splice(index, 1)

    // If the element doesn't have an ID it means it's a new element. Remove it from the form
    // but don't add it to the deleted list
    if (!deletedInspectionPoint[0].id) return

    deletedInspectionPoints.value.push(deletedInspectionPoint[0])
  }
}

const removeFailureType = (ft: IEquipmentFailureTypeUpsertForm) => {
  const index = equipmentTypeForm.failure_types.indexOf(ft)
  if (index !== -1) {
    const deletedFailureType = equipmentTypeForm.failure_types.splice(index, 1)

    // If the element doesn't have an ID it means it's a new element. Remove it from the form
    // but don't add it to the deleted list
    if (!deletedFailureType[0].id) return

    deletedFailureTypes.value.push(deletedFailureType[0])
  }
}

const removeRepairAction = (ra: IEquipmentRepairActionUpsertForm) => {
  const index = equipmentTypeForm.repair_actions.indexOf(ra)
  if (index !== -1) {
    const deletedRepairAction = equipmentTypeForm.repair_actions.splice(index, 1)

    // If the element doesn't have an ID it means it's a new element. Remove it from the form
    // but don't add it to the deleted list
    if (!deletedRepairAction[0].id) return

    deletedRepairActions.value.push(deletedRepairAction[0])
  }
}

/**
 * Handles form submit.
 *
 * @param formEl form element ref.
 */
const submitForm = async (formEl: FormInstance | undefined) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  formEl?.validate(async (valid, fields) => {
    if (!valid) {
      return
    }

    try {
      if (props.equipmentType) {
        const operations = [
          updateEquipmentTypeMutation.mutateAsync({
            id: props.equipmentType.id,
            form: {
              name: equipmentTypeForm.name,
              inspection_points: equipmentTypeForm.inspection_points,
              failure_types: equipmentTypeForm.failure_types,
              repair_actions: equipmentTypeForm.repair_actions,
            },
          }),
        ]

        if (deletedInspectionPoints.value.length > 0) {
          operations.push(
            deleteInspectionPointsMutation.mutateAsync({
              equipmentTypeID: props.equipmentType.id,
              form: { ids: deletedInspectionPoints.value.map((ip) => ip.id!) },
            })
          )
        }

        if (deletedFailureTypes.value.length > 0) {
          operations.push(
            deleteFailureTypesMutation.mutateAsync({
              equipmentTypeID: props.equipmentType.id,
              form: { ids: deletedFailureTypes.value.map((ft) => ft.id!) },
            })
          )
        }

        if (deletedRepairActions.value.length > 0) {
          operations.push(
            deleteRepairActionsMutation.mutateAsync({
              equipmentTypeID: props.equipmentType.id,
              form: { ids: deletedRepairActions.value.map((ra) => ra.id!) },
            })
          )
        }

        await Promise.all(operations)
      } else {
        await createEquipmentTypeMutation.mutateAsync(equipmentTypeForm)
      }
      resetFormCloseDialog()
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (any) {
      // Error message handled by mutation
    }
  })
}

/**
 * Resets form, closes dialog.
 */
const resetFormCloseDialog = () => {
  equipmentTypeFormRef.value?.resetFields()
  equipmentTypeForm.inspection_points = []
  equipmentTypeForm.failure_types = []
  equipmentTypeForm.repair_actions = []
  deletedInspectionPoints.value = []
  deletedFailureTypes.value = []
  deletedRepairActions.value = []
  model.value = false
}
</script>

<template>
  <el-dialog
    v-model="model"
    :title="`${equipmentType ? 'Update' : 'Create'} Equipment Type`"
    :show-close="false"
    :width="580"
    @open="initForm"
    @closed="resetFormCloseDialog"
  >
    <el-form
      ref="equipmentTypeFormRef"
      :model="equipmentTypeForm"
      :rules="rules"
      label-width="auto"
      label-position="left"
    >
      <el-form-item label="Name" prop="name">
        <el-input v-model="equipmentTypeForm.name" placeholder="Name" clearable />
      </el-form-item>
      <!-- Inspection Points creation form item -->
      <el-form-item label="Inspection Points" prop="inspection_points">
        <div class="w-full flex flex-col gap-4">
          <el-form-item v-for="(ip, index) in equipmentTypeForm.inspection_points" :key="ip.key">
            <div class="w-full inline-flex gap-2">
              <el-form-item
                :key="`${ip.key}.name`"
                class="flex-grow"
                :prop="`inspection_points.${index}.name`"
                :rules="{
                  required: true,
                  message: 'Inspection point name is required.',
                  trigger: 'change',
                }"
              >
                <el-input v-model="ip.name" placeholder="Name" filterable clearable />
              </el-form-item>
              <div class="inline-flex justify-center items-center">
                <el-button link :icon="Delete" @click="removeInspectionPoint(ip)" />
              </div>
            </div>
          </el-form-item>
          <el-button class="self-start" link type="primary" @click="addInspectionPoint"
            >+ Add inspection point</el-button
          >
        </div>
      </el-form-item>
      <!-- Failure Types creation form item -->
      <el-form-item label="Failure Types" prop="failure_types">
        <div class="w-full flex flex-col gap-4">
          <el-form-item v-for="(elem, index) in equipmentTypeForm.failure_types" :key="elem.key">
            <div class="w-full inline-flex gap-2">
              <el-form-item
                :key="`${elem.key}.description`"
                class="flex-grow"
                :prop="`failure_types.${index}.description`"
                :rules="{
                  required: true,
                  message: 'Failure type description is required.',
                  trigger: 'change',
                }"
              >
                <el-input v-model="elem.description" placeholder="Description" filterable clearable />
              </el-form-item>
              <div class="inline-flex justify-center items-center">
                <el-button link :icon="Delete" @click="removeFailureType(elem)" />
              </div>
            </div>
          </el-form-item>
          <el-button class="self-start" link type="primary" @click="addFailureType">+ Add failure type</el-button>
        </div>
      </el-form-item>
      <!-- Repair Actions creation form item -->
      <el-form-item label="Repair Actions" prop="repair_actions">
        <div class="w-full flex flex-col gap-4">
          <el-form-item v-for="(elem, index) in equipmentTypeForm.repair_actions" :key="elem.key">
            <div class="w-full inline-flex gap-2">
              <el-form-item
                :key="`${elem.key}.description`"
                class="flex-grow"
                :prop="`repair_actions.${index}.description`"
                :rules="{
                  required: true,
                  message: 'Repair action description is required.',
                  trigger: 'change',
                }"
              >
                <el-input v-model="elem.description" placeholder="Description" filterable clearable />
              </el-form-item>
              <div class="inline-flex justify-center items-center">
                <el-button link :icon="Delete" @click="removeRepairAction(elem)" />
              </div>
            </div>
          </el-form-item>
          <el-button class="self-start" link type="primary" @click="addRepairAction">+ Add repair action</el-button>
        </div>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="resetFormCloseDialog">Cancel</el-button>
      <el-button type="primary" @click="submitForm(equipmentTypeFormRef)">Submit</el-button>
    </template>
  </el-dialog>
</template>
